import { useTranslation } from 'react-i18next';
import React from 'react';

import { Grid, Typography } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { ConnectedDatePicker } from '@libs/ui/components/form/datepicker';
import { ConnectedRadioGroup } from '@libs/ui/components/form/radio/Radio';
import { ConnectedSelect } from '@libs/ui/components/form/select';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import StepSection from '@libs/ui/components/step-section';

import { SocialSecurityId, LocationCodes, WorkerPayType } from '../components';
import { ApplicationFormState } from '../types';

interface DetailsSectionProps {
  showOnlyRequired: boolean;
  job: Job;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({
  showOnlyRequired,
  job,
}) => {
  const { t } = useTranslation('jobForm');

  const YES_NO_OPTIONS = [
    { value: 'true', text: t('applicationForm.details.yes') },
    { value: 'false', text: t('applicationForm.details.no') },
  ];

  const EDUCATION_OPTIONS = [
    { text: t('applicationForm.details.education.highSchool'), value: 'high_school' },
    { text: t('applicationForm.details.education.associate'), value: 'associate' },
    { text: t('applicationForm.details.education.bachelor'), value: 'bachelor' },
    { text: t('applicationForm.details.education.master'), value: 'master' },
    { text: t('applicationForm.details.education.doctorate'), value: 'doctorate' },
  ];

  const PREVIOUS_WORK_OPTIONS = [
    { text: t('applicationForm.details.previousWorked.no'), value: 'No' },
    { text: t('applicationForm.details.previousWorked.contractor'), value: 'Yes - Contractor' },
    { text: t('applicationForm.details.previousWorked.employee'), value: 'Yes - Employee' },
    { text: t('applicationForm.details.previousWorked.retiree'), value: 'Yes - Retiree' },
  ];

  const RECRUITING_SOURCE_OPTIONS = [
    {
      text: t('applicationForm.details.recruitingSourceOptions.careerCenter'),
      value: 'Career Center (unemployment office)',
    },
    { text: t('applicationForm.details.recruitingSourceOptions.careerFair'), value: 'Career Fair' },
    { text: t('applicationForm.details.recruitingSourceOptions.employeeReferral'), value: 'Employee referral' },
    {
      text: t('applicationForm.details.recruitingSourceOptions.jobBoard'),
      value: 'Job Board (LinkedIn, Indeed, etc.)',
    },
    {
      text: t('applicationForm.details.recruitingSourceOptions.collegeJobBoard'),
      value: 'Local College/ University Job Board or Career Fair',
    },
    { text: t('applicationForm.details.recruitingSourceOptions.maximusReferral'), value: 'MAXIMUS referral' },
    { text: t('applicationForm.details.recruitingSourceOptions.nonProfit'), value: 'Non Profit Agency (501c3)' },
    { text: t('applicationForm.details.recruitingSourceOptions.onlineAdvertising'), value: 'On-line Advertising' },
    {
      text: t('applicationForm.details.recruitingSourceOptions.umaInitiative'),
      value: 'United Medical Academy (UMA) Initiative',
    },
    { text: t('applicationForm.details.recruitingSourceOptions.veteranAgency'), value: 'Veteran Sourcing Agency' },
  ];

  const VETERAN_OPTIONS = [
    {
      text: t('applicationForm.details.declinedToDisclose'),
      value: 'Candidate declined to disclose',
    },
    { text: t('applicationForm.details.no'), value: 'No' },
    { text: t('applicationForm.details.yes'), value: 'Yes' },
  ];

  return (
    <StepSection index={2} title={t('applicationForm.details.title')}>
      <Grid spacing={4} direction="column" container>
        <SocialSecurityId
          job={job as Job}
          showOnlyRequired={showOnlyRequired}
        />

        {/* Hidden security ID fields that will be auto-calculated */}
        <Grid item sx={{ display: 'none' }}>
          <ConnectedTextField
            name={modelPath<ApplicationFormState>((i) => i.details.securityId)}
            fullWidth
            variant="filled"
            label={t('applicationForm.details.securityId')}
            disabled
          />
        </Grid>

        <Grid item>
          <ConnectedDatePicker
            name={modelPath<ApplicationFormState>(
              (i) => i.details.availableDate,
            )}
            inputFormat="MM/dd/yyyy"
            TextFieldProps={{
              variant: 'filled',
              fullWidth: true,
              label: (
                <Typography>
                  {t('applicationForm.details.availableDate')}{' '}
                  <Typography component="span" color="error">
                    *
                  </Typography>
                </Typography>
              ),
            }}
          />
        </Grid>

        <Grid item>
          <Typography variant="body2" gutterBottom>
            {t('applicationForm.details.submittedToOther')}{' '}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <ConnectedRadioGroup
            name={modelPath<ApplicationFormState>(
              (i) => i.details.submittedToOtherJobs,
            )}
            options={YES_NO_OPTIONS}
          />
        </Grid>

        <WorkerPayType job={job} showOnlyRequired={showOnlyRequired} />

        <Grid item>
          <Typography variant="body2" gutterBottom>
            {t('applicationForm.details.displayWorkforce')}{' '}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <ConnectedRadioGroup
            name={modelPath<ApplicationFormState>(
              (i) => i.details.displayWorkforceRecord,
            )}
            options={YES_NO_OPTIONS}
          />
        </Grid>

        {!showOnlyRequired && (
          <>
            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>(
                  (i) => i.details.education,
                )}
                options={EDUCATION_OPTIONS}
                fullWidth
                variant="filled"
                label={t('applicationForm.details.collegeEducation')}
              />
            </Grid>

            <Grid item>
              <ConnectedTextField
                name={modelPath<ApplicationFormState>((i) => i.details.county)}
                fullWidth
                variant="filled"
                label={t('applicationForm.details.county')}
              />
            </Grid>

            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>(
                  (i) => i.details.previouslyWorked,
                )}
                options={PREVIOUS_WORK_OPTIONS}
                fullWidth
                variant="filled"
                label={t('applicationForm.details.previouslyWorked')}
              />
            </Grid>
          </>
        )}

        <LocationCodes job={job} showOnlyRequired={showOnlyRequired} />

        {!showOnlyRequired && (
          <>
            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>(
                  (i) => i.details.recruitingSource,
                )}
                options={RECRUITING_SOURCE_OPTIONS}
                fullWidth
                variant="filled"
                label={t('applicationForm.details.recruitingSource')}
              />
            </Grid>

            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>((i) => i.details.veteran)}
                options={VETERAN_OPTIONS}
                fullWidth
                variant="filled"
                label={t('applicationForm.details.veteran')}
              />
            </Grid>
          </>
        )}
      </Grid>
    </StepSection>
  );
};

export default DetailsSection;
